import { useCallback } from 'react';
import { Button, Stack, Text } from '@adc/parallax-component-library';
import { Dialog } from '@tamagui/dialog';

import { ModalContent } from '~/components/ModalContent';
import { ModalOverlay } from '~/components/ModalOverlay';
import { useModal } from '~/features/modals/useModal';
import { buildTestId } from '~/utils/buildTestId';

type ArchiveArchivePatientModalProps = {
  testID: string;
  onArchive: () => Promise<void>;
};

export const ArchivePatientModal = ({
  testID,
  onArchive,
}: ArchiveArchivePatientModalProps) => {
  const { closeModal } = useModal();

  const onClose = useCallback(() => {
    closeModal('ArchivePatient');
  }, [closeModal]);

  const onOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        onClose();
      }
    },
    [onClose],
  );

  const handlePatientArchive = useCallback(async () => {
    await onArchive();
    onClose();
  }, [onClose, onArchive]);

  return (
    <Dialog open onOpenChange={onOpenChange} modal>
      <Dialog.Portal testID={buildTestId({ testID, suffix: 'portal' })}>
        <ModalOverlay testID={buildTestId({ testID, suffix: 'overlay' })} />

        <ModalContent
          testID={buildTestId({
            testID,
            suffix: 'content',
          })}
          key="content"
          maxWidth={800}
          gap="$6"
        >
          <Text type="$h3.medium">Confirm Archive Patient</Text>
          <Text type="$bodyBase.medium">
            Archiving removes the patient from your list and stops collecting
            lactate data. You can still view past reports and restore patients
            from the Archive folder. The patient will be notified that you are
            no longer connected to them.
          </Text>
          <Stack
            testID={buildTestId({ testID, suffix: 'actions' })}
            flexDirection="row"
            justifyContent="flex-end"
            gap="$4"
          >
            <Button
              label="Cancel"
              aria-label="Cancel Patient Archivation"
              variant="secondary"
              testID={buildTestId({
                testID,
                suffix: ['cancel', 'button'],
              })}
              onPress={onClose}
            />
            <Button
              label="Archive Patient"
              aria-label="Archive Patient"
              variant="primary"
              testID={buildTestId({
                testID,
                suffix: ['archive', 'button'],
              })}
              onPress={() => void handlePatientArchive()}
            />
          </Stack>
        </ModalContent>
      </Dialog.Portal>
    </Dialog>
  );
};
